/** Lifecycle buckets used on My Properties. */

export const PROPERTY_STATUS = {
  active: {
    key: 'active',
    label: 'Active',
    hint: 'Live and bookable',
    color: '#15803d',
    bg: 'rgba(22, 163, 74, 0.14)',
  },
  review: {
    key: 'review',
    label: 'In Review',
    hint: 'Waiting for TabTrips approval',
    color: '#b45309',
    bg: 'rgba(245, 158, 11, 0.16)',
  },
  inactive: {
    key: 'inactive',
    label: 'Deactivate',
    hint: 'Taken off the live site',
    color: '#475569',
    bg: 'rgba(100, 116, 139, 0.16)',
  },
  draft: {
    key: 'draft',
    label: 'Draft',
    hint: 'Unfinished listing',
    color: '#c2410c',
    bg: 'rgba(255, 119, 0, 0.16)',
  },
};

export const PROPERTY_STATUS_ORDER = ['active', 'review', 'inactive', 'draft'];

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function asText(value) {
  return String(value ?? '').toLowerCase().trim();
}

function isTruthyFlag(value) {
  if (value === true || value === 1) return true;
  const text = asText(value);
  return text === '1' || text === 'true' || text === 'yes' || text === 'active' || text === 'live';
}

function isFalsyFlag(value) {
  if (value === false || value === 0) return true;
  const text = asText(value);
  return text === '0' || text === 'false' || text === 'no' || text === 'inactive';
}

function looksLikeReview(text) {
  if (!text) return false;
  if (text === 'approved' || text.includes('approved') && !text.includes('unapproved')) return false;
  return (
    text.includes('review') ||
    text.includes('pending') ||
    text.includes('await') ||
    text === 'submitted' ||
    text === 'unapproved'
  );
}

function looksLikeInactive(text) {
  if (!text) return false;
  return (
    text.includes('inactive') ||
    text.includes('disable') ||
    text.includes('deactiv') ||
    text.includes('offline') ||
    text.includes('hidden') ||
    text.includes('reject') ||
    text.includes('block') ||
    text.includes('suspend')
  );
}

function looksLikeActive(text) {
  if (!text) return false;
  return (
    text === 'active' ||
    text === 'live' ||
    text === 'approved' ||
    text === 'published' ||
    text === 'enabled'
  );
}

/**
 * Map a hotel (or local draft) onto Active / In Review / Deactivate / Draft.
 * The host-hotels payload has used several field names over time, so this
 * reads the common ones rather than assuming a single key.
 */
export function classifyPropertyStatus(hotel) {
  if (!hotel) return 'active';
  if (hotel.draft === true) return 'draft';

  const rawStatus = firstDefined(
    hotel.listing_status,
    hotel.hotel_status,
    hotel.approval_status,
    hotel.admin_status,
    hotel.publish_status,
    hotel.live_status,
    hotel.property_status,
    hotel.status
  );
  const statusText = asText(rawStatus);

  const approved = firstDefined(
    hotel.is_approved,
    hotel.approved,
    hotel.isApproved,
    hotel.admin_approved,
    hotel.verified
  );

  if (looksLikeInactive(statusText)) return 'inactive';
  if (looksLikeReview(statusText)) return 'review';
  if (statusText === '2') return 'inactive';

  if (approved !== undefined && !isTruthyFlag(approved)) {
    if (looksLikeInactive(asText(approved))) return 'inactive';
    return 'review';
  }

  const isActive = firstDefined(hotel.isActive, hotel.is_active, hotel.active, hotel.live, hotel.is_live);

  if (statusText === '0') {
    if (isFalsyFlag(isActive)) return 'inactive';
    return 'review';
  }

  if (isFalsyFlag(isActive)) return 'inactive';
  if (looksLikeActive(statusText) || isTruthyFlag(isActive) || isTruthyFlag(approved)) return 'active';

  return 'active';
}

export function getPropertyStatusMeta(statusKey) {
  return PROPERTY_STATUS[statusKey] || PROPERTY_STATUS.active;
}
